/**
 * Phase GUESSING : monte GuessBoard sur le blob recu. Le hit/miss est calcule
 * localement pour un retour immediat, mais les points affiches sont ceux que le
 * serveur renvoie : lui seul fait autorite.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { hostIsStreamer, isCollab, isContest, isElimination, type RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { playerById, useRoomStore } from '../store/roomStore.js';
import { GuessBoard } from '../game/GuessBoard.js';
import { HuntBoard } from '../game/HuntBoard.js';
import { ContestBoard } from '../game/ContestBoard.js';
import { CollabBoard } from '../game/CollabBoard.js';
import { DevOptionsStore } from '../game/devOptions.js';
import { loadArtwork } from '../game/artwork.js';
import { Avatar } from '../ui/Avatar.js';
import { Screen } from '../ui/Screen.js';
import { roundLabel } from '../ui/roundLabel.js';
import { GameLayout } from '../ui/GameLayout.js';
import { Watching } from '../ui/Watching.js';
import { OwnRetouch } from '../ui/OwnRetouch.js';

export function GuessingScreen({ room }: { room: RoomSnapshot }): ReactElement {
  const t = useT();
  const target = useRoomStore((state) => state.guessTarget);
  const result = useRoomStore((state) => state.guessResult);
  const playerId = useRoomStore((state) => state.playerId);
  const submitted = useRoomStore((state) => state.submitted);
  const hunt = useRoomStore((state) => state.hunt);
  const collab = useRoomStore((state) => state.collab);
  const contest = useRoomStore((state) => state.contest);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const devStore = useMemo(() => new DevOptionsStore(), []);

  useEffect(() => {
    if (target === null) return;
    let alive = true;
    setImage(null);
    loadArtwork(target.file)
      .then((loaded) => {
        if (alive) setImage(loaded);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [target]);

  const onGuess = useCallback((x: number, y: number) => {
    connection.send({ type: 'guess', x, y });
  }, []);

  // Battle royale : pas de cible individuelle, un seul tableau que tout le
  // monde fouille en meme temps. Les elimines et les arrivants le voient
  // aussi : ils regardent la chasse continuer sans eux, ce qui vaut mieux
  // qu'un ecran d'attente. Leurs clics sont refuses des deux cotes du fil.
  const me = room.players.find((player) => player.id === playerId);
  // En mode stream, le public a cache ses retouches et regarde le streamer les
  // chercher : il est spectateur de cette phase au meme titre qu'un elimine,
  // meme s'il joue bel et bien la partie.
  const streamAudience = hostIsStreamer(room.settings.mode) && me?.streamer === false;
  const spectating = me?.waiting === true || me?.eliminated === true || streamAudience;
  // Le streamer cherche SANS LIMITE : il est le seul chercheur de sa manche,
  // son premier rate fermait la phase pour toute la table. Lui annoncer la
  // regle du clic unique serait donc faux (cf. `huntGuess` cote serveur).
  const oneMissApplies = me?.streamer !== true;

  // Collaboratif : personne ne cherche, on juge N tableaux construits a
  // plusieurs. Le plateau rejoue chaque retouche avant d'ouvrir le vote.
  if (isCollab(room.settings.mode) && collab !== null) {
    return (
      <Screen name="collab-vote">
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{t('collab.boards')}</strong>
              <span>{t('collab.pickBest')}</span>
            </header>
            <CollabBoard
              room={room}
              boards={collab.boards}
              drawings={collab.drawings}
              selfId={playerId}
              mine={collab.mine}
              tally={collab.tally}
              endsAt={room.endsAt}
              durationMs={room.settings.guessTime * 1000}
              onVote={(board, slot) => connection.send({ type: 'vote', slot, board })}
            />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  // Concours : meme transport que la chasse (un tableau, une retouche par
  // joueur), mais on juge au lieu de chercher.
  if (isContest(room.settings.mode) && hunt !== null && hunt.round === room.round) {
    return (
      <Screen name={`contest-${hunt.round}`}>
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{roundLabel(t, room, hunt.round)}</strong>
              <span>{t('contest.title')}</span>
              <small className="guessing__rule">{t('contest.subtitle')}</small>
            </header>
            <ContestBoard
              key={hunt.round}
              room={room}
              file={hunt.file}
              targets={hunt.targets}
              drawings={hunt.drawings}
              selfId={playerId}
              myVote={contest.mine}
              tally={null}
              durationMs={room.settings.guessTime * 1000}
              onVote={(slot) => connection.send({ type: 'vote', slot })}
            />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  if (hunt !== null && hunt.round === room.round) {
    return (
      <Screen name={`hunt-${hunt.round}`}>
        <GameLayout room={room} selfId={playerId}>
          <div className="guessing">
            <header className="guessing__header">
              <strong className="board__round">{roundLabel(t, room, hunt.round)}</strong>
              {/* Le spectateur voit la meme chasse, mais il ne la joue pas :
                  lui annoncer la regle du clic unique serait un contresens. */}
              <span>{spectating ? t('spectator.hunt') : t('hunt.title')}</span>
              {!spectating && oneMissApplies && (
                <small className="guessing__rule">{t('hunt.oneMiss')}</small>
              )}
            </header>
            <HuntBoard
              key={hunt.round}
              room={room}
              file={hunt.file}
              targets={hunt.targets}
              drawings={hunt.drawings}
              found={hunt.found}
              {...(isElimination(room.settings.mode) ? { foundOrder: hunt.foundOrder } : {})}
              selfId={playerId}
              frozen={false}
              watching={spectating}
              guessTimeMs={room.settings.guessTime * 1000}
              onStrike={onGuess}
            />
          </div>
        </GameLayout>
      </Screen>
    );
  }

  const drawer = playerById(room, target?.drawerId ?? null);

  // Le dessin arrive en binaire juste apres le JSON : tant qu'il n'est pas la,
  // afficher le tableau serait montrer une zone blanche, donc la reponse.
  // Aucune cible : spectateur elimine, ou dessinateur de la manche en mode
  // « un seul dessine ». Il regarde les autres chercher SA retouche.
  const self = room.players.find((player) => player.id === playerId);
  if (target === null) {
    return (
      <Screen name="guessing-watching">
        <GameLayout room={room} selfId={playerId}>
          {/* Le dessinateur de la manche n'est pas « verrouille » : il est en
              train d'etre cherche, ce qui est une position active. */}
          <Watching
            title={
              self?.waiting === true
                ? t('game.joinedTitle')
                : self?.eliminated === true
                  ? t('game.spectator')
                  : t('game.beingSought')
            }
            subtitle={
              self?.waiting === true
                ? t('game.joinedHunt')
                : self?.eliminated === true
                  ? t('game.spectatorHint')
                  : t('game.beingSoughtHint')
            }
            room={room}
            durationMs={room.settings.guessTime * 1000}
          >
            {/* Le dessinateur de la manche revoit sa retouche, en lecture
                seule : c'est elle que les autres traquent. */}
            {self?.eliminated !== true && submitted !== null && submitted.round === room.round && (
              <OwnRetouch
                file={submitted.file}
                zone={submitted.zone}
                palette={submitted.palette}
                drawing={submitted.drawing}
              />
            )}
          </Watching>
        </GameLayout>
      </Screen>
    );
  }

  if (image === null || target.drawing === null) {
    return (
      <Screen name="guessing-loading">
        <p className="sandbox__status">{t('join.connecting')}</p>
      </Screen>
    );
  }

  // `done` vient du serveur : c'est lui qui sait qu'on a deja joue, y compris
  // apres une reconnexion en pleine phase.
  if (self?.done === true && result === null) {
    return (
      <Screen name="guessing-waiting">
        <GameLayout room={room} selfId={playerId}>
        <main className="waiting">
          <p className="waiting__title">{t('guessing.locked')}</p>
          <p className="waiting__subtitle">{t('guessing.waiting')}</p>
        </main>
        </GameLayout>
      </Screen>
    );
  }

  return (
    <Screen name={`guessing-${target.round}`}>
      <GameLayout room={room} selfId={playerId}>
      <div className="guessing">
        <header className="guessing__header">
          <strong className="board__round">{roundLabel(t, room, target.round)}</strong>
          {drawer !== null && (
            <Avatar playerId={drawer.id} avatar={drawer.avatar} colorIndex={drawer.colorIndex} size={28} />
          )}
          <span>{t('guessing.title', { name: drawer?.nickname ?? '?' })}</span>
        </header>

        <GuessBoard
          key={`${target.file}-${target.round}`}
          image={image}
          zone={target.zone}
          buffer={target.drawing}
          palette={target.palette}
          dev={devStore}
          endsAt={room.endsAt}
          guessTimeMs={room.settings.guessTime * 1000}
          onGuess={onGuess}
          serverPoints={
            result === null ? null : { guesser: result.guesserPoints, drawer: result.drawerPoints }
          }
        />
      </div>
      </GameLayout>
    </Screen>
  );
}
